// Expandable NAV Flows / NAV Asset matrix - ports reporting-hub's
// NavCalculationMatrix.tsx. `entry` is the single `type: "nav_calculation"`
// calculation entry (see nav_calculation.py / NavCalculationEntry in
// reporting-hub/frontend/src/lib/types.ts): one row per platform plus a
// "TOTAL" row, each with nav_flows/nav_asset (NavValue: value + steps[]) and
// a diff (nav_flows - nav_asset, a plausibility check, not an error state).

import React, { useState } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { formatCurrency, titleCasePlatform } from './calcFormat';

const HeaderTooltip = ({ label, formula }) => (
  <span
    className="cr-nav-th-tooltip cr-help-underline"
    data-pr-tooltip={formula}
    data-pr-position="top"
  >
    {label}
  </span>
);

const formatNullableCurrency = (value) => (value === null || value === undefined ? '–' : formatCurrency(value));

const rowLabel = (platform) => (platform === 'TOTAL' ? 'Total' : titleCasePlatform(platform));

const StepsTable = ({ title, nav }) => (
  <table className="cr-steps-table">
    <caption className="cr-steps-caption">
      {title}
    </caption>
    <tbody>
      {(nav.steps || []).map((step, i) => (
        <tr key={`${step.label}-${i}`}>
          <td className="cr-step-label">
            ({step.sign}) {step.label}
          </td>
          <td className="cr-right">{formatNullableCurrency(step.value)}</td>
        </tr>
      ))}
      <tr>
        <td className="cr-step-total">= {title}</td>
        <td className="cr-right cr-step-total">{formatNullableCurrency(nav.value)}</td>
      </tr>
    </tbody>
  </table>
);

const NavCalculationMatrix = ({ entry }) => {
  const [openRows, setOpenRows] = useState(new Set());

  if (!entry || !entry.rows) return null;

  const toggle = (platform) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  };

  return (
    <div className="cr-table-frame">
      <Tooltip target=".cr-nav-th-tooltip" />
      <table className="cr-data-table">
        <thead>
          <tr>
            <th>Originator</th>
            <th className="cr-right">
              <HeaderTooltip
                label="NAV (Flows)"
                formula="Paid-In Gross + Total Interest Received (Since Launch) - EAD + Recovery Principal + Remaining Expected Recovery - Cost (Cumulative, simulated) - Cost Realized (Paid) - Misc Bank Cost"
              />
            </th>
            <th className="cr-right">
              <HeaderTooltip
                label="NAV (Asset)"
                formula="Total Investment + From Pending Loans - Outstanding Bids + (Originator + SPV Cash) - Cost (= General Overview's NAV)"
              />
            </th>
            <th className="cr-right">
              <HeaderTooltip label="Diff" formula="NAV (Flows) - NAV (Asset), a plausibility check - not an error state" />
            </th>
            <th style={{ width: '1.5rem' }} />
          </tr>
        </thead>
        <tbody>
          {entry.rows.map((row) => {
            const isTotal = row.platform === 'TOTAL';
            const isOpen = openRows.has(row.platform);
            return (
              <React.Fragment key={row.platform}>
                <tr
                  onClick={() => toggle(row.platform)}
                  className={[isTotal ? 'is-total' : '', isOpen ? 'is-open' : ''].filter(Boolean).join(' ') || undefined}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="cr-strong-cell">{rowLabel(row.platform)}</td>
                  <td className="cr-right">
                    {formatNullableCurrency(row.nav_flows && row.nav_flows.value)}
                  </td>
                  <td className="cr-right">
                    {formatNullableCurrency(row.nav_asset && row.nav_asset.value)}
                  </td>
                  <td className="cr-right">{formatNullableCurrency(row.diff)}</td>
                  <td className="cr-right cr-chevron-cell">
                    <i
                      className={`pi pi-chevron-right cr-row-chevron${isOpen ? ' is-open' : ''}`}
                    />
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={5} className="cr-expand-cell">
                      <div className="flex flex-wrap gap-5">
                        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                          <StepsTable title="NAV (Flows)" nav={row.nav_flows || { steps: [], value: null }} />
                        </div>
                        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                          <StepsTable title="NAV (Asset)" nav={row.nav_asset || { steps: [], value: null }} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default NavCalculationMatrix;
